import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import BlockIcon from "@material-ui/icons/Block";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WarningIcon from "@material-ui/icons/Warning";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import VerificationDetails, { latestRequest } from "./VerificationDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { Avatar, readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const VERIFICATION_FILTERS = `
  _id: $_id
  status: $status
  fullName: $fullName
  phoneNumber: $phoneNumber
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
`;

const VERIFICATION_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $status: DriverVerificationRequestsStatus
  $fullName: String
  $phoneNumber: String
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
`;

const GET_VERIFICATIONS = gql`
  query getDriversVerificationRequestsByAdmin(${VERIFICATION_VARIABLES}) {
    getDriversVerificationRequestsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${VERIFICATION_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      _id
      fullName
      email
      phoneNumber
      gender
      birthDate
      state
      createdAt
      verificationRequests {
        status
        submitDate
        verificationDetails {
          profileImageUrl
          gender
          birthDate
          address {
            full
            zipCode
          }
          drivingLicence {
            photoUrl
            licenceId
            expireDate
          }
          drivingRecordPhotoUrl
          canadianVerificationPhotoUrl
          canadianVerificationExpireDate
          backgroundCheckDocumentPhotoUrl
        }
      }
      defaultCar {
        _id
        plate
        carType {
          type
        }
      }
      drivingLicence {
        licenceId
        expireDate
      }
    }
    getDriversVerificationRequestsCountByAdmin(filters: { ${VERIFICATION_FILTERS} })
  }
`;

const GET_QUEUE_COUNTS = gql`
  query getDriverVerificationCounts {
    pending: getDriversVerificationRequestsCountByAdmin(filters: { status: PENDING })
    approved: getDriversVerificationRequestsCountByAdmin(filters: { status: APPROVED })
    rejected: getDriversVerificationRequestsCountByAdmin(filters: { status: REJECTED })
  }
`;

const APPROVE_VERIFICATION = gql`
  mutation approveDriverVerificationRequestByAdmin($driverId: ID!) {
    approveDriverVerificationRequestByAdmin(driverId: $driverId) {
      _id
      fullName
    }
  }
`;

const REJECT_VERIFICATION = gql`
  mutation rejectDriverVerificationRequestByAdmin($driverId: ID!, $message: String) {
    rejectDriverVerificationRequestByAdmin(driverId: $driverId, message: $message) {
      _id
      fullName
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const QUEUE_OPTIONS = [
  { value: "PENDING", label: "Awaiting review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];
const QUEUE_LABELS = QUEUE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = { APPROVED: "success", REJECTED: "danger", PENDING: "warning" };

const SEARCH_TARGETS = [
  { value: "fullName", label: "Name" },
  { value: "phoneNumber", label: "Phone" },
];
const SEARCH_LABELS = { fullName: "Name", phoneNumber: "Phone" };

const SORT_OPTIONS = [
  { value: "oldest", label: "Longest waiting" },
  { value: "newest", label: "Newest first" },
];

const FILTER_DEBOUNCE_MS = 350;

const waitingFor = (value) => {
  if (!value) return "";
  const days = moment().diff(moment(value), "days");
  return days <= 0 ? "today" : `${days} day${days === 1 ? "" : "s"} ago`;
};

// Anything the reviewer should notice before approving.
const documentWarnings = (driver) => {
  const request = latestRequest(driver);
  const details = (request && request.verificationDetails) || {};
  const licence = details.drivingLicence || driver.drivingLicence || {};
  const warnings = [];
  if (licence.expireDate && moment(licence.expireDate).isBefore(moment())) {
    warnings.push("Licence expired");
  }
  if (
    details.canadianVerificationExpireDate &&
    moment(details.canadianVerificationExpireDate).isBefore(moment())
  ) {
    warnings.push("Work permit expired");
  }
  if (!details.backgroundCheckDocumentPhotoUrl) warnings.push("No background check");
  if (!details.drivingRecordPhotoUrl) warnings.push("No driving record");
  return warnings;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

/**
 * Verifications - the driver verification queue.
 *
 * Approve and reject are both on the row now. Rejection used to live in
 * a separate RejectForm component that also carried a shop-rejection
 * branch it was never asked to run from here; the message is required
 * either way, since the driver is shown it.
 */
const Verifications = () => {
  const [queue, setQueue] = useState("PENDING");
  const [searchTarget, setSearchTarget] = useState("fullName");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("oldest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [detailTarget, setDetailTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState("");
  const [rejectionError, setRejectionError] = useState("");
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionResult, setActionResult] = useState("");

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    status: "PENDING",
    createdAtSort: 1,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        fullName: undefined,
        phoneNumber: undefined,
        ...(term ? { [searchTarget]: term } : {}),
        status: queue,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [queue, searchTarget, searchTerm, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_VERIFICATIONS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_QUEUE_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [approveVerification] = useMutation(APPROVE_VERIFICATION);
  const [rejectVerification] = useMutation(REJECT_VERIFICATION);

  const drivers = (data && data.getDriversVerificationRequestsByAdmin) || [];
  const totalCount = (data && data.getDriversVerificationRequestsCountByAdmin) || 0;
  const counts = countsData || {};

  /* ---------------- actions ---------------- */

  const finish = (message) => {
    setWorking(false);
    setApproveTarget(null);
    setRejectTarget(null);
    setDetailTarget(null);
    setRejectionMessage("");
    setActionResult(message);
    refetch();
    refetchCounts();
  };

  const runApprove = () => {
    if (!approveTarget) return;
    setWorking(true);
    setActionError("");
    setActionResult("");
    approveVerification({ variables: { driverId: approveTarget._id } })
      .then(() => finish(`${approveTarget.fullName} is verified and can start driving.`))
      .catch((err) => {
        setWorking(false);
        setApproveTarget(null);
        setActionError(readableError(err));
      });
  };

  const runReject = () => {
    if (!rejectTarget) return;
    const message = rejectionMessage.trim();
    if (!message) {
      setRejectionError("Tell the driver what needs fixing — they are shown this message.");
      return;
    }
    setRejectionError("");
    setWorking(true);
    setActionError("");
    setActionResult("");
    rejectVerification({ variables: { driverId: rejectTarget._id, message } })
      .then(() => finish(`${rejectTarget.fullName} was sent back with your notes.`))
      .catch((err) => {
        setWorking(false);
        setRejectTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Joined: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superPeoplePage superVerifyPage">
        <main className="main">
          <PageHeader title="Driver verifications" description="Drivers waiting to be approved." />
          <MetricsSkeleton count={3} />
          <TableSkeleton rows={6} columns={5} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superPeoplePage superVerifyPage">
      <main className="main">
        <PageHeader
          title="Driver verifications"
          count={totalCount}
          description="Drivers waiting to be approved. Approving lets them start accepting trips; rejecting sends them back with your notes."
        />

        <section className="stats">
          <MetricCard
            icon={<HourglassEmptyIcon />}
            title="Awaiting review"
            value={counts.pending || 0}
            tone={counts.pending > 0 ? "warning" : "default"}
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Rejected"
            value={counts.rejected || 0}
            tone={counts.rejected > 0 ? "danger" : "default"}
          />
          <MetricCard
            icon={<VerifiedUserIcon />}
            title="Approved"
            value={counts.approved || 0}
            tone="success"
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Queue: ${QUEUE_LABELS[queue]}`}
              options={QUEUE_OPTIONS}
              value={queue}
              active={false}
              onChange={(e) => setQueue(e.target.value)}
            />
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <DateRangeFilter
              label="Joined"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft" />
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh queue"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}
        {actionResult && (
          <div className="dsFormMessage success" role="status">
            {actionResult}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superPeopleSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Driver</div>
              <div className="tableHeaderCell">Contact</div>
              <div className="tableHeaderCell">Submitted</div>
              <div className="tableHeaderCell">Documents</div>
              <div className="tableHeaderCell tableHeaderAction">Decision</div>
            </div>

            {drivers.length === 0 ? (
              <EmptyState
                icon={<VerifiedUserIcon />}
                title={queue === "PENDING" ? "Nothing waiting on you" : "Nothing here"}
                message={
                  queue === "PENDING"
                    ? "Every driver who has applied has had a decision."
                    : "No drivers in this queue match your search."
                }
              />
            ) : (
              drivers.map((driver) => {
                const request = latestRequest(driver);
                const warnings = documentWarnings(driver);
                const isPending = !request || request.status === "PENDING";
                return (
                  <div className="row" key={driver._id}>
                    <div className="superPersonCell" data-label="Driver">
                      <Avatar name={driver.fullName} />
                      <span className="superPersonText">
                        <span className="superPersonName">{driver.fullName || "Unnamed"}</span>
                        <span className="superPersonMeta">
                          {driver.defaultCar ? driver.defaultCar.plate : "No vehicle"}
                        </span>
                      </span>
                    </div>

                    <div className="superPersonText" data-label="Contact">
                      <span className="superPersonName">{driver.phoneNumber || "No phone"}</span>
                      <span className="superPersonMeta">{driver.email || "No email"}</span>
                    </div>

                    <span className="updated" data-label="Submitted">
                      {request && request.submitDate ? (
                        <React.Fragment>
                          <strong>{moment(request.submitDate).format("MMM D, YYYY")}</strong>
                          <span>{waitingFor(request.submitDate)}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superPersonMuted">Not submitted</span>
                      )}
                    </span>

                    <span className="superPersonStack" data-label="Documents">
                      <StatusBadge
                        status={STATUS_TONES[request && request.status] || "neutral"}
                        label={
                          request && request.status
                            ? QUEUE_LABELS[request.status] || request.status
                            : "No request"
                        }
                      />
                      {warnings.length > 0 && (
                        <StatusBadge
                          status="warning"
                          label={
                            warnings.length === 1 ? warnings[0] : `${warnings[0]} +${warnings.length - 1}`
                          }
                          dot={false}
                        />
                      )}
                    </span>

                    <div className="superPersonActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setDetailTarget(driver)}
                        title="Review documents"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                      {isPending && (
                        <React.Fragment>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setRejectTarget(driver)}
                            disabled={working}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setApproveTarget(driver)}
                            disabled={working}
                          >
                            Approve
                          </Button>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(detailTarget)}
        onClose={() => setDetailTarget(null)}
        title="Verification details"
        subtitle={detailTarget ? detailTarget.fullName : ""}
        size="lg"
      >
        {detailTarget && (
          <React.Fragment>
            {documentWarnings(detailTarget).length > 0 && (
              <div className="dsFormMessage warning" role="status">
                <WarningIcon />
                <span>{documentWarnings(detailTarget).join(" · ")}</span>
              </div>
            )}
            <VerificationDetails item={detailTarget} />
            <div className="superPersonFooter">
              <Button variant="secondary" onClick={() => setRejectTarget(detailTarget)}>
                Reject
              </Button>
              <Button
                variant="primary"
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                onClick={() => setApproveTarget(detailTarget)}
              >
                Approve
              </Button>
            </div>
          </React.Fragment>
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(approveTarget)}
        onClose={() => (working ? null : setApproveTarget(null))}
        title="Approve driver"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setApproveTarget(null), disabled: working },
          { label: "Approve", onClick: runApprove, variant: "primary", loading: working },
        ]}
      >
        <div className="superPersonConfirm">
          {approveTarget ? approveTarget.fullName : "This driver"} will be verified and can start
          accepting trips straight away.
        </div>
      </DialogShell>

      <DialogShell
        open={Boolean(rejectTarget)}
        onClose={() => {
          if (working) return;
          setRejectTarget(null);
          setRejectionError("");
        }}
        title="Reject verification"
        subtitle={rejectTarget ? rejectTarget.fullName : ""}
        actions={[
          {
            label: "Cancel",
            onClick: () => {
              setRejectTarget(null);
              setRejectionError("");
            },
            disabled: working,
          },
          { label: "Reject", onClick: runReject, variant: "danger", loading: working },
        ]}
      >
        <div className="superPersonForm" style={{ width: 460, maxWidth: "100%" }}>
          <Field
            label="What needs fixing?"
            htmlFor="rejectionMessage"
            error={rejectionError}
            hint="The driver is shown this and can resubmit their documents."
            required
          >
            <textarea
              id="rejectionMessage"
              className="dsInput"
              rows={4}
              value={rejectionMessage}
              onChange={(e) => setRejectionMessage(e.target.value)}
              placeholder="For example: the driving licence photo is cut off at the edges."
            />
          </Field>
        </div>
      </DialogShell>
    </div>
  );
};

export default Verifications;
